import * as zod from 'zod';

export const DDS_DOMAIN_MIN = 1;
/**
 * Max domain whose CycloneDDS default port mapping (base = 7400 + 250*d,
 * we open base..base+100) stays under 65535. Domain 0 is the internal
 * loopback domain and is never user-configurable.
 */
export const DDS_DOMAIN_MAX = 232;

/** Interfaces that user DDS config must never touch. */
export const DDS_PROTECTED_INTERFACE_PREFIXES = [
  'internal', // CAN bus (arm joints)
  'direct', // iPad USB-C (managed by feathers-bot)
  'lo',
  'docker',
  'veth',
  'br-',
] as const;

export function isDdsEligibleInterface(name: string): boolean {
  return !DDS_PROTECTED_INTERFACE_PREFIXES.some((prefix) =>
    name.startsWith(prefix),
  );
}

/** ufw port range owned by a managed DDS domain, e.g. '8650:8750/udp'. */
export function ddsUfwRuleSpec(domainId: number): string {
  const base = 7400 + 250 * domainId;

  return `${base}:${base + 100}/udp`;
}

export const DdsApplyState = zod.enum(['applied', 'applying', 'failed']);
export type DdsApplyState = zod.infer<typeof DdsApplyState>;

export const DdsApplyError = zod.object({
  step: zod.string(),
  message: zod.string(),
});
export type DdsApplyError = zod.infer<typeof DdsApplyError>;

// Shared field fragments, reused by DdsSettingsConfig (top level and
// priorConfig) and DdsConfigurationRequest so the bounds/shape can never
// drift apart.
const ddsDomainIdSchema = zod
  .number()
  .int()
  .min(DDS_DOMAIN_MIN)
  .max(DDS_DOMAIN_MAX);

const ddsInterfacesSchema = zod
  .array(zod.string().regex(/^[A-Za-z0-9@._-]+$/))
  .nonempty()
  .refine((ifaces) => ifaces.every(isDdsEligibleInterface), {
    message: 'interfaces must not include protected interfaces',
  });

/**
 * Exact ufw rule specs this feature added, e.g. ['8650:8750/udp'].
 * Items are interpolated into a root shell command (`--remove-rule`),
 * so the strict pattern makes that closure provable.
 */
const ddsOwnedUfwRulesSchema = zod.array(zod.string().regex(/^\d+:\d+\/udp$/));

const ddsMaterializedHashesSchema = zod
  .object({ cycloneddsXml: zod.string(), bridgeYaml: zod.string() })
  .optional();

/**
 * Stored as the JSONB `data` blob on `dds_settings`. The DB row is the
 * source of truth for the user's desired external-DDS config; the updater
 * force-overwrites cyclonedds.xml on upgrade, so feathers-bot reconciles
 * materialized files against this record on boot (when `managed`).
 */
export const DdsSettingsConfig = zod.object({
  /** false until the user first edits — pure-Ansible behavior until opt-in */
  managed: zod.boolean(),
  domainId: ddsDomainIdSchema,
  interfaces: ddsInterfacesSchema,
  applyState: DdsApplyState,
  // 'failed' = apply failed and files/ufw were rolled back (lastError.step
  // says where; step 'rollback' means the rollback itself failed — the
  // on-disk manifest is left for boot recovery). On 'failed' the row's
  // domainId/interfaces are reverted to the restored live config — the
  // attempted values are not persisted (exception: step 'persist', where
  // the new config IS live and the row keeps it).
  lastError: DdsApplyError.optional(),
  ownedUfwRules: ddsOwnedUfwRulesSchema,
  materializedHashes: ddsMaterializedHashesSchema,
  /**
   * Last-known-live config, persisted together with the 'applying' patch:
   * while an apply is in flight the row's domainId/interfaces hold the
   * apply's TARGET values, and this snapshot is what a failure reverts to.
   * Persisting it (rather than holding it in memory) means the revert
   * survives a feathers-bot crash mid-apply — boot recovery reads it back.
   * Cleared whenever the row reaches a terminal applyState.
   */
  priorConfig: zod
    .object({
      domainId: ddsDomainIdSchema,
      interfaces: ddsInterfacesSchema,
      ownedUfwRules: ddsOwnedUfwRulesSchema,
      materializedHashes: ddsMaterializedHashesSchema,
    })
    .optional(),
});
export type DdsSettingsConfig = zod.infer<typeof DdsSettingsConfig>;

export const DdsSettingsData = zod.object({
  id: zod.string(),
  robotID: zod.string(),
  data: DdsSettingsConfig,
});
export type DdsSettingsData = zod.infer<typeof DdsSettingsData>;

/** What the user is allowed to submit via UI/API. */
export const DdsConfigurationRequest = zod.object({
  domainId: ddsDomainIdSchema,
  interfaces: ddsInterfacesSchema,
  pin: zod.string().optional(),
});
export type DdsConfigurationRequest = zod.infer<typeof DdsConfigurationRequest>;

/**
 * Matches the shipped Ansible cyclonedds.xml (Domain 1 on the wired NIC
 * only). Seed fallback for when that file cannot be read; otherwise unmanaged
 * rows mirror the live file at feathers-bot boot (see initializeDdsSettings).
 */
export const DDS_SETTINGS_DEFAULTS: Omit<DdsSettingsData, 'id' | 'robotID'> = {
  data: {
    managed: false,
    domainId: 1,
    interfaces: ['external0'],
    applyState: 'applied',
    ownedUfwRules: [],
  },
};
